import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Camera, ImagePlus, Leaf, Loader2, X } from "lucide-react";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ease = [0.22, 1, 0.36, 1];

export default function SelfieWall() {
  const [selfies, setSelfies] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [caption, setCaption] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    axios
      .get(`${API}/selfies`)
      .then(({ data }) => setSelfies(data))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  const pickFile = (f) => {
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Pick an image file (JPG, PNG, WebP, GIF).");
      return;
    }
    if (preview) URL.revokeObjectURL(preview);
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const submit = async () => {
    if (!file) return toast.error("Add your nature selfie first.");
    if (!name.trim()) return toast.error("Give your post a name or trail alias.");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("name", name.trim());
      fd.append("caption", caption.trim());
      const { data } = await axios.post(`${API}/selfies`, fd);
      setSelfies((s) => [data, ...s]);
      toast.success("Posted to the Selfie Wall — bonus points claimed.");
      setOpen(false);
      setName("");
      setCaption("");
      setFile(null);
      setPreview(null);
    } catch {
      toast.error("Upload failed — the forest connection dropped.");
    } finally {
      setUploading(false);
    }
  };

  const cheer = async (id) => {
    try {
      const { data } = await axios.post(`${API}/selfies/${id}/cheer`);
      setSelfies((s) => s.map((x) => (x.id === id ? { ...x, cheers: data.cheers } : x)));
    } catch {}
  };

  return (
    <section id="selfies" className="px-5 pb-24 md:px-12">
      <div className="mx-auto max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.7, ease }}
          className="mb-10 flex flex-col gap-6 md:flex-row md:items-end md:justify-between"
        >
          <div>
            <p className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-moss">
              <Camera className="h-4 w-4" /> The Selfie Wall
            </p>
            <h2 className="mt-3 font-display text-3xl tracking-tight text-cream sm:text-4xl lg:text-5xl">
              Proof of grass contact.
            </h2>
          </div>
          <div className="flex items-center gap-4">
            <p className="max-w-xs text-sm leading-relaxed text-forest-dim">
              Bonus-point receipts from people who actually went outside. Add yours.
            </p>
            <button
              data-testid="selfie-upload-open-button"
              onClick={() => setOpen(true)}
              className="group inline-flex shrink-0 items-center gap-2 rounded-full bg-moss px-6 py-3 font-semibold text-forest-950 transition-all hover:-translate-y-0.5 hover:shadow-[0_0_35px_rgba(82,183,136,0.35)]"
            >
              <ImagePlus className="h-4 w-4" />
              Add yours
            </button>
          </div>
        </motion.div>

        {!loaded ? (
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="shimmer-bg animate-shimmer aspect-square rounded-2xl" />
            ))}
          </div>
        ) : selfies.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-forest-500/60 py-16 text-center">
            <Camera className="mx-auto h-8 w-8 text-forest-500" />
            <p className="mt-4 font-display text-xl text-cream">No selfies yet.</p>
            <p className="mt-2 text-sm text-forest-muted">
              Be the first proof of grass contact on the wall.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
            {selfies.map((s, i) => (
              <motion.figure
                key={s.id}
                data-testid="selfie-card"
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.5, delay: (i % 4) * 0.06, ease }}
                className="group relative aspect-square overflow-hidden rounded-2xl border border-forest-600/50 bg-forest-800"
              >
                <img
                  src={`${API}/selfies/${s.id}/image`}
                  alt={s.caption || `${s.name}'s nature selfie`}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-forest-950/95 via-forest-950/60 to-transparent p-3 pt-8">
                  <p className="truncate font-display text-sm text-cream">{s.name}</p>
                  {s.caption && (
                    <p className="mt-0.5 truncate text-[11px] text-forest-dim">{s.caption}</p>
                  )}
                  <button
                    data-testid={`selfie-cheer-${i}`}
                    onClick={() => cheer(s.id)}
                    className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-forest-500/60 bg-forest-950/70 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.12em] text-moss transition-colors hover:border-moss"
                  >
                    <Leaf className="h-3 w-3" />
                    {s.cheers} cheers
                  </button>
                </figcaption>
              </motion.figure>
            ))}
          </div>
        )}
      </div>

      <AnimatePresence>
        {open && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => !uploading && setOpen(false)}
              className="fixed inset-0 z-[60] bg-forest-950/70 backdrop-blur-sm"
            />
            <motion.div
              data-testid="selfie-upload-modal"
              initial={{ opacity: 0, y: 40, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 24, scale: 0.97 }}
              transition={{ duration: 0.4, ease }}
              className="fixed left-1/2 top-1/2 z-[61] w-[calc(100%-2.5rem)] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-3xl border border-forest-500/50 bg-forest-900 p-6 shadow-2xl"
            >
              <div className="flex items-center justify-between">
                <h3 className="font-display text-xl text-cream">Post your grass proof</h3>
                <button
                  data-testid="selfie-upload-close-button"
                  onClick={() => setOpen(false)}
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-forest-600/50 text-cream transition-colors hover:border-moss/70 hover:text-moss"
                  aria-label="Close upload"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <button
                data-testid="selfie-file-dropzone"
                onClick={() => fileRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  pickFile(e.dataTransfer.files?.[0]);
                }}
                className="mt-5 flex h-40 w-full items-center justify-center overflow-hidden rounded-2xl border border-dashed border-forest-500/60 bg-forest-800/60 text-forest-muted transition-colors hover:border-moss/70"
              >
                {preview ? (
                  <img src={preview} alt="Selfie preview" className="h-full w-full object-cover" />
                ) : (
                  <span className="flex flex-col items-center gap-2 text-xs">
                    <ImagePlus className="h-6 w-6 text-moss" />
                    Tap to choose or drop an image
                  </span>
                )}
              </button>
              <input
                ref={fileRef}
                data-testid="selfie-file-input"
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                className="hidden"
                onChange={(e) => pickFile(e.target.files?.[0])}
              />

              <Input
                data-testid="selfie-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name or trail alias"
                className="mt-4 h-11 w-full rounded-xl border-forest-500/50 bg-forest-800/80 text-cream placeholder:text-forest-muted focus-visible:border-moss/70 focus-visible:ring-moss/30 focus-visible:ring-offset-forest-950"
              />
              <Input
                data-testid="selfie-caption-input"
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                placeholder="Caption (e.g. Sunday at Cubbon Park)"
                className="mt-3 h-11 w-full rounded-xl border-forest-500/50 bg-forest-800/80 text-cream placeholder:text-forest-muted focus-visible:border-moss/70 focus-visible:ring-moss/30 focus-visible:ring-offset-forest-950"
              />

              <button
                data-testid="selfie-submit-button"
                onClick={submit}
                disabled={uploading}
                className="mt-5 inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-moss font-semibold text-forest-950 transition-transform hover:scale-[1.01] active:scale-[0.99] disabled:opacity-70"
              >
                {uploading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Posting…
                  </>
                ) : (
                  <>
                    <Leaf className="h-4 w-4" /> Claim bonus points
                  </>
                )}
              </button>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </section>
  );
}
